import {
  useState,
  useEffect,
  useCallback,
  useRef,
  MouseEvent as ReactMouseEvent,
} from 'react'
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import {
  faCircleCheck,
  faTriangleExclamation,
  faBan,
  faSpinner,
  faCaretDown,
} from '@fortawesome/pro-solid-svg-icons'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'

import { Badge, Icon, Input, Text, type BadgeColor } from '@/ui'
import { TypographyStyles } from '@/styles'
import { useDebounce } from '@/utils/sharedHooks'
import { useAnalyticsTrack } from '@/features/amplitude'
import { searchPlaidInstitutions } from './bank-compatibility.actions'
import {
  classifyInstitution,
  DEFAULT_SUGGESTIONS,
} from './bank-compatibility.constants'
import {
  FiTier,
  type AccountSubtype,
  type SelectedInstitution,
  type PlaidInstitutionResult,
} from './bank-compatibility.types'
import { BankCompatibilityDialog } from './BankCompatibilityDialog'

export const TIER_COLOR_MAP: Record<FiTier, string> = {
  [FiTier.WELL_SUPPORTED]: 'green',
  [FiTier.LIMITED_SUPPORT]: 'yellow',
  [FiTier.NOT_SUPPORTED]: 'red',
}

const TIER_BADGE_CONFIG: Record<
  FiTier,
  { color: BadgeColor; icon: IconDefinition }
> = {
  [FiTier.WELL_SUPPORTED]: {
    color: 'green',
    icon: faCircleCheck,
  },
  [FiTier.LIMITED_SUPPORT]: {
    color: 'yellow',
    icon: faTriangleExclamation,
  },
  [FiTier.NOT_SUPPORTED]: {
    color: 'orange',
    icon: faBan,
  },
}

export type BankAccountType = 'business' | 'personal_willing_to_separate'

interface BankCompatibilityProps {
  selectedInstitutions: SelectedInstitution[]
  onInstitutionsChange: (institutions: SelectedInstitution[]) => void
  /** Account subtype derived from the parent form's "accountsSeparate" field */
  accountSubtype?: AccountSubtype
  /** Used for Amplitude event properties */
  bankAccountType: BankAccountType
}

export const BankCompatibility = ({
  selectedInstitutions,
  onInstitutionsChange,
  accountSubtype = 'business',
  bankAccountType,
}: BankCompatibilityProps) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] =
    useState<PlaidInstitutionResult[]>(DEFAULT_SUGGESTIONS)
  const [isSearching, setIsSearching] = useState(false)
  const [dialogInstitution, setDialogInstitution] =
    useState<SelectedInstitution | null>(null)
  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const hasInteractedRef = useRef(false)
  const track = useAnalyticsTrack()

  // Fire "viewed bank compatibility check" once on mount
  useEffect(() => {
    track('viewed bank compatibility check', {
      bank_account_type: bankAccountType,
    })
  }, [track, bankAccountType])

  const debouncedQuery = useDebounce(searchQuery, 300)

  useEffect(() => {
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setSearchResults(DEFAULT_SUGGESTIONS)
      setIsSearching(false)
      return undefined
    }

    let cancelled = false
    setIsSearching(true)

    searchPlaidInstitutions(debouncedQuery)
      .then((results) => {
        if (!cancelled) {
          setSearchResults(Array.isArray(results) ? results : [])
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSearchResults([])
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsSearching(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [debouncedQuery])

  // Reset active index when results change
  useEffect(() => {
    setActiveIdx(null)
  }, [searchResults])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        e.target instanceof Node &&
        !containerRef.current.contains(e.target)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = useCallback(
    (institutionId: string) => {
      if (selectedInstitutions.some((i) => i.institutionId === institutionId)) {
        return
      }

      const result = searchResults.find(
        (r) => r.institutionId === institutionId
      )
      if (!result) return

      const tier = classifyInstitution(institutionId)
      const institution: SelectedInstitution = {
        institutionId,
        name: result.name,
        tier,
        accountSubtype,
      }

      onInstitutionsChange([...selectedInstitutions, institution])
      track('completed bank compatibility check', {
        compatibility_result: TIER_COLOR_MAP[tier],
        bank_account_type: bankAccountType,
        unsupported_bank_name:
          tier === FiTier.NOT_SUPPORTED ? result.name : null,
      })
      setSearchQuery('')
      setSearchResults(DEFAULT_SUGGESTIONS)

      // Close the popover first, then open the dialog after the popover
      // portal unmounts. Without this delay both portals render in the
      // same frame and the dropdown stays visible behind the dialog.
      setOpen(false)
      requestAnimationFrame(() => {
        setDialogInstitution(institution)
      })
    },
    [
      selectedInstitutions,
      searchResults,
      onInstitutionsChange,
      accountSubtype,
      track,
      bankAccountType,
    ]
  )

  const handleRemove = useCallback(
    (institutionId: string) => {
      onInstitutionsChange(
        selectedInstitutions.filter((i) => i.institutionId !== institutionId)
      )
    },
    [selectedInstitutions, onInstitutionsChange]
  )

  // Filter out already-selected institutions from dropdown options
  const filteredResults = searchResults.filter(
    (r) =>
      !selectedInstitutions.some((s) => s.institutionId === r.institutionId)
  )

  // Build a synthetic ID for the "not compatible" fallback option
  const unsupportedId = searchQuery.trim()
    ? `unsupported_${searchQuery.trim().toLowerCase().replace(/\s+/g, '_')}`.slice(
        0,
        50
      )
    : ''

  // Show a "Not Compatible" creatable option when the query doesn't
  // exactly match a Plaid result and isn't already selected.
  const showNotCompatibleOption =
    searchQuery.trim().length >= 2 &&
    !isSearching &&
    !searchResults.some(
      (r) => r.name.toLowerCase() === searchQuery.trim().toLowerCase()
    ) &&
    !selectedInstitutions.some((i) => i.institutionId === unsupportedId)

  // Total selectable items including the optional fallback row
  const totalOptions =
    filteredResults.length + (showNotCompatibleOption ? 1 : 0)

  const handleSelectNotCompatible = useCallback(() => {
    if (!unsupportedId) return
    if (selectedInstitutions.some((i) => i.institutionId === unsupportedId))
      return

    const institution: SelectedInstitution = {
      institutionId: unsupportedId,
      name: searchQuery.trim(),
      tier: FiTier.NOT_SUPPORTED,
      accountSubtype,
    }
    onInstitutionsChange([...selectedInstitutions, institution])
    track('completed bank compatibility check', {
      compatibility_result: 'red',
      bank_account_type: bankAccountType,
      unsupported_bank_name: searchQuery.trim(),
    })
    setSearchQuery('')
    setSearchResults(DEFAULT_SUGGESTIONS)
    setOpen(false)
    requestAnimationFrame(() => {
      setDialogInstitution(institution)
    })
  }, [
    unsupportedId,
    selectedInstitutions,
    searchQuery,
    accountSubtype,
    onInstitutionsChange,
    track,
    bankAccountType,
  ])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || totalOptions === 0) return

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setActiveIdx((prev) =>
          prev === null ? 0 : Math.min(prev + 1, totalOptions - 1)
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setActiveIdx((prev) =>
          prev === null ? totalOptions - 1 : Math.max(prev - 1, 0)
        )
        break
      case 'Enter':
        e.preventDefault()
        if (activeIdx !== null) {
          if (activeIdx < filteredResults.length) {
            handleSelect(filteredResults[activeIdx].institutionId)
          } else if (showNotCompatibleOption) {
            handleSelectNotCompatible()
          }
        }
        break
      case 'Escape':
        e.preventDefault()
        setOpen(false)
        break
      default:
        break
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <Text variant={TypographyStyles.LABEL_MD}>
        Check bank compatibility{' '}
        <span className="text-xs font-normal text-neutral-400">(optional)</span>
      </Text>
      <Text variant={TypographyStyles.BODY_SM} className="text-neutral-400">
        Tell us who you bank with, and we&apos;ll let you know the level of
        support you can expect from us. You&apos;ll connect it in a later step.
      </Text>

      <div className="relative" ref={containerRef}>
        <Input
          ref={inputRef}
          role="combobox"
          aria-expanded={open && totalOptions > 0}
          aria-autocomplete="list"
          aria-activedescendant={
            activeIdx !== null ? `bank-option-${activeIdx}` : undefined
          }
          name="bankSearch"
          type="search"
          placeholder="Search for your bank..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value)
            setOpen(true)
          }}
          onFocus={() => {
            setOpen(true)
            if (!hasInteractedRef.current) {
              hasInteractedRef.current = true
              track('interacted bank compatibility check', {
                bank_account_type: bankAccountType,
              })
            }
          }}
          onKeyDown={handleKeyDown}
          iconRight={isSearching ? faSpinner : faCaretDown}
        />

        {open && totalOptions > 0 && (
          <div
            role="listbox"
            className="absolute z-50 mt-1 w-full flex flex-col p-1 max-h-[300px] overflow-y-auto rounded-md border bg-neutral-0 text-neutral-600 shadow-md border-neutral-900"
          >
            {filteredResults.map((result, idx) => (
              <div
                key={result.institutionId}
                id={`bank-option-${idx}`}
                role="option"
                tabIndex={-1}
                aria-selected={idx === activeIdx}
                className={`flex select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm hover:bg-neutral-25 hover:cursor-pointer ${
                  idx === activeIdx ? 'bg-neutral-25' : ''
                }`}
                onMouseDown={() => handleSelect(result.institutionId)}
              >
                {result.name}
              </div>
            ))}

            {showNotCompatibleOption && (
              <div
                id={`bank-option-${filteredResults.length}`}
                role="option"
                tabIndex={-1}
                aria-selected={activeIdx === filteredResults.length}
                className={`flex select-none items-center justify-between rounded-sm py-1.5 pl-2 pr-2 text-sm hover:bg-neutral-25 hover:cursor-pointer ${
                  activeIdx === filteredResults.length ? 'bg-neutral-25' : ''
                }`}
                onMouseDown={handleSelectNotCompatible}
              >
                <span>&quot;{searchQuery.trim()}&quot;</span>
                <Badge color="orange" variant="secondary" size="sm">
                  Not Compatible
                </Badge>
              </div>
            )}
          </div>
        )}
      </div>

      {selectedInstitutions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedInstitutions.map((inst) => {
            const config = TIER_BADGE_CONFIG[inst.tier]
            return (
              <Badge
                key={inst.institutionId}
                color={config.color}
                variant="secondary"
                icon={config.icon}
                className="cursor-pointer"
                data-testid={`badge-${inst.institutionId}`}
                onClick={() => setDialogInstitution(inst)}
              >
                {inst.name}
                <Icon
                  icon={faXmark}
                  color="currentColor"
                  className="cursor-pointer pl-2 w-3 h-3"
                  onClick={(e: ReactMouseEvent) => {
                    e.stopPropagation()
                    handleRemove(inst.institutionId)
                  }}
                />
              </Badge>
            )
          })}
        </div>
      )}

      <BankCompatibilityDialog
        institution={dialogInstitution}
        onClose={() => setDialogInstitution(null)}
      />
    </div>
  )
}
